import "./styles.css";
import TrainingCard from "./trainingCard";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { instance } from "../../../utils/utils";
import { format, parse, addDays } from "date-fns";
import { es } from "date-fns/locale";
import Tooltip from "../../tooltip/tooltip";

const TrainingPlanPanel = ({ membershipId, userId }) => {
  const navigate = useNavigate();
  const groupedDays = [
    [0, 1], // Domingo - Lunes
    [2, 3], // Martes - Miércoles
    [4, 5], // Jueves - Viernes
    [6], // Sábado
  ];

  const [trainingPlanId, setTrainingPlanId] = useState(null);
  const [itemsGrouped, setItemsGrouped] = useState({});
  const [weekStart, setWeekStart] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    const todayIndex = new Date().getDay();
    const groupIndex = groupedDays.findIndex((group) =>
      group.includes(todayIndex)
    );
    return groupIndex !== -1 ? groupIndex : 0;
  });

  useEffect(() => {
    if (!membershipId && !userId) return;

    const fetchTrainingPlan = async () => {
      try {
        const hasValidUserId =
          userId !== null && userId !== undefined && userId > 0;

        const params = hasValidUserId
          ? { member_id: userId }
          : { membership_id: membershipId };

        const res = await instance.get("memberships/new/training_plan", {
          params,
        });

        const planId = res.data?.training_plan?.id;
        if (planId) setTrainingPlanId(planId);
      } catch (error) {
        console.error("❌ Error al obtener plan de entrenamiento:", error);
      }
    };

    fetchTrainingPlan();
  }, [membershipId, userId]);

  useEffect(() => {
    if (!trainingPlanId) return;

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;

    const fetchPublishedSessions = async () => {
      try {
        const res = await instance.get(
          "memberships/new/training_plan/published",
          {
            params: {
              training_plan_id: trainingPlanId,
              year,
              month,
            },
          }
        );

        setItemsGrouped(res.data.items_grouped || {});
        setWeekStart(res.data.week_start);
      } catch (error) {
        console.error("❌ Error al obtener sesiones publicadas:", error);
      }
    };

    fetchPublishedSessions();
  }, [trainingPlanId]);

  const handlePrev = () => {
    if (currentPage > 0) setCurrentPage((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentPage < groupedDays.length - 1)
      setCurrentPage((prev) => prev + 1);
  };

  const getStartOfWeekSunday = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 (domingo) a 6 (sábado)
    return addDays(d, -day); // retrocede hasta domingo
  };

  const baseDate = weekStart
    ? parse(weekStart, "dd-MM-yyyy", new Date())
    : new Date();

  const weekBaseDate = getStartOfWeekSunday(baseDate);

  // Agrupar todas las sesiones por fecha real
  const allSessions = Object.values(itemsGrouped).flat();
  const sessionsByDate = allSessions.reduce((acc, session) => {
    acc[session.launch_date] = acc[session.launch_date] || [];
    acc[session.launch_date].push(session);
    return acc;
  }, {});

  return (
    <div
      className="training-plan-container"
      style={{
        maxWidth: window.matchMedia("(max-width: 1350px)").matches
          ? "auto"
          : "18rem",
      }}
    >
      <div className="d-flex flex-column gap-3">
        <span className="training-plan-title">Plan de Entrenamiento</span>
          <button
              className="training-plan-button justify-content-center"
              onClick={() =>
                  navigate("/dashboard/training-panel", {
                    state: { membershipId, userId },
                  })
              }
              disabled={false}
          >
            Panel de entrenamiento
          </button>
        <span className="training-plan-title">
          Entrenamientos durante la semana
        </span>
      </div>

      <div className="training-plan-line"></div>

      <div className="row w-100 align-items-start overflow-y-auto">
        {groupedDays[currentPage].map((dayIndex) => {
          const date = addDays(weekBaseDate, dayIndex);
          const formattedDate = format(date, "dd-MM-yyyy");
          const dayLabel = format(date, "EEEE", { locale: es });
          const displayDate = format(date, "dd 'de' MMMM", { locale: es });
          const sessions = sessionsByDate[formattedDate] || [];

          return (
            <div
              key={dayIndex}
              className="col-6 d-flex flex-column justify-content-center align-items-center gap-3 px-1"
            >
              <div className="training-plan-view-day-title gap-2">
                <span className="training-plan-view-day-label">
                  {dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)}
                </span>
                <span className="training-plan-view-day-date">
                  {displayDate}
                </span>
              </div>

              <div className="d-flex flex-column justify-content-center align-items-center gap-3 w-100">
                {sessions.length === 0 ? (
                  <small className="training-plan-view-day-date">
                    Sin sesiones
                  </small>
                ) : (
                  sessions.map((session) => (
                    <TrainingCard
                      key={session.id}
                      id={session.id}
                      date={session.launch_date}
                      type={session.class_name}
                      coach={session.teacher_name}
                      status={session.status}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="training-plan-pagination gap-1">
        <button
          className="training-plan-button-pagination"
          onClick={handlePrev}
          disabled={currentPage === 0}
        >
          <i className="bi bi-chevron-left text-white"></i>
        </button>
        <button
          className="training-plan-button-pagination"
          onClick={handleNext}
          disabled={currentPage >= groupedDays.length - 1}
        >
          <i className="bi bi-chevron-right text-white"></i>
        </button>
      </div>
    </div>
  );
};

export default TrainingPlanPanel;
